/**
 * Storage URL resolution — one way to turn a storage path into something
 * renderable.
 *
 * Public buckets (avatars, vehicles, org-logos, vehicle-makes) can use
 * `supabase.storage.from(b).getPublicUrl(path)` directly. PRIVATE buckets
 * cannot: `getPublicUrl` happily returns a URL that always 400s, which is
 * how dispute evidence, inspection photos, and vehicle documents ended up
 * rendering as blanks. Private buckets must go through a signed URL.
 *
 * Rows may hold either a bare storage path (current writers) or a full URL
 * (legacy rows written before this split), so both are accepted and only
 * paths get signed.
 */
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'

/** Seconds a signed URL stays valid. One hour covers a screen session. */
const SIGNED_URL_TTL_SECONDS = 3600

function isAbsoluteUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://')
}

/**
 * Rows written before a bucket was made private hold a `.../object/public/
 * {bucket}/{path}` URL, which stops resolving the moment the bucket flips.
 * Recover the storage path from those so they can be signed like any other.
 * Returns null when the value is not a public URL for this bucket.
 */
function publicUrlToPath(bucket: string, value: string): string | null {
  const marker = `/storage/v1/object/public/${bucket}/`
  const at = value.indexOf(marker)
  if (at === -1) return null
  const path = value.slice(at + marker.length).split('?')[0]
  return path ? decodeURIComponent(path) : null
}

/** Storage path for signing, or null when the value is an unrelated URL. */
function toSignablePath(bucket: string, value: string): string | null {
  if (!isAbsoluteUrl(value)) return value
  return publicUrlToPath(bucket, value)
}

/**
 * Sign one storage path. Returns the input unchanged when it is already an
 * absolute URL. Throws ServiceError when signing fails, so callers surface a
 * real error instead of rendering a silent blank.
 */
export async function signedUrl(bucket: string, pathOrUrl: string): Promise<string> {
  const path = toSignablePath(bucket, pathOrUrl)
  if (path === null) return pathOrUrl

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS)

  if (error || !data?.signedUrl) {
    throw new ServiceError({
      message: `Signed URL failed for ${bucket}: ${error?.message ?? 'no url returned'}`,
      code: 'STORAGE_SIGN_FAILED',
      statusCode: 500,
      cause: error,
    })
  }
  return data.signedUrl
}

/**
 * Sign many paths from the same bucket in one round trip, preserving order.
 * A path the API returns no signature for falls back to the raw value rather
 * than dropping the item, so list lengths stay stable for callers whose rows
 * enforce array-length parity.
 */
export async function signedUrls(bucket: string, pathsOrUrls: string[]): Promise<string[]> {
  const pathByInput = new Map<string, string>()
  for (const value of pathsOrUrls) {
    const path = toSignablePath(bucket, value)
    if (path !== null) pathByInput.set(value, path)
  }
  const toSign = [...new Set(pathByInput.values())]
  if (toSign.length === 0) return pathsOrUrls

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrls(toSign, SIGNED_URL_TTL_SECONDS)

  if (error) {
    throw new ServiceError({
      message: `Signed URLs failed for ${bucket}: ${error.message}`,
      code: 'STORAGE_SIGN_FAILED',
      statusCode: 500,
      cause: error,
    })
  }

  const byPath = new Map((data ?? []).map((item) => [item.path, item.signedUrl]))
  return pathsOrUrls.map((value) => {
    const path = pathByInput.get(value)
    return path ? (byPath.get(path) ?? value) : value
  })
}
